import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Sparkles, Users, CalendarHeart, Music2, Cake, Briefcase, Phone } from "lucide-react";
import { api, ASSETS } from "@/lib/api";
import { RESTAURANT } from "@/lib/restaurant";
import SEO from "@/components/SEO";

const EVENT_TYPES = [
  { v: "Anniversaire", icon: Cake },
  { v: "Mariage / Fiançailles", icon: CalendarHeart },
  { v: "Baptême / Communion", icon: Sparkles },
  { v: "Repas d'entreprise / Séminaire", icon: Briefcase },
  { v: "Soirée dansante", icon: Music2 },
  { v: "Autre", icon: Sparkles },
];

const NEEDS = ["DJ / Musique live", "Décoration personnalisée", "Gâteau / Pièce montée", "Menu sur mesure", "Vidéoprojecteur", "Bar à cocktails"];

export default function Privatisation() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    event_type: "Anniversaire", date: "", guests: 20, budget: "", needs: [], message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const minDate = new Date().toISOString().split("T")[0];

  const toggleNeed = (n) => setForm({ ...form, needs: form.needs.includes(n) ? form.needs.filter(x => x !== n) : [...form.needs, n] });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.guests) < RESTAURANT.privatization.minGuests) {
      toast.error(`Privatisation à partir de ${RESTAURANT.privatization.minGuests} personnes`);
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/privatization", { ...form, guests: Number(form.guests) });
      setDone(res.data);
      toast.success("Demande envoyée !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'envoi.");
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="privatization-success" className="pt-32 pb-24 min-h-[80vh]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#C04E38]/10 grid place-items-center text-[#C04E38] mb-6">
            <CheckCircle2 size={40} />
          </div>
          <div className="eyebrow">Obrigado {done.name} !</div>
          <h1 className="text-4xl md:text-5xl mt-3">Votre demande est reçue</h1>
          <p className="text-[#5C5A56] mt-5 text-lg">
            Nous avons bien reçu votre demande pour <b>{done.event_type}</b> le <b>{done.date}</b>
            ({done.guests} personnes). Notre équipe vous rappelle dans les plus brefs délais
            pour préparer ensemble un événement à votre image.
          </p>
          <div className="card-warm bg-[#1F476B] !text-white border-0 p-6 mt-8 inline-flex items-center gap-3">
            <Phone size={18} className="text-[#FFD7A6]" />
            <a href={`tel:${RESTAURANT.phone.replace(/\s/g,'')}`} className="text-white"><b>{RESTAURANT.phone}</b> — réponse rapide</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="privatization-page">
      <SEO title="Privatisation salle" description="Privatisez la salle du Globo Dancing à Trappes pour vos anniversaires, mariages, baptêmes ou repas d'entreprise — à partir de 12 personnes. Devis sur mesure." path="/privatisation"/>
      {/* Hero */}
      <section className="pt-32 pb-12 azulejo-bg">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="eyebrow">Privatização</div>
            <h1 className="text-5xl md:text-7xl mt-4 leading-[0.95]">
              Privatisez la <em className="italic text-[#C04E38]">salle du fond</em>
            </h1>
            <p className="text-[#5C5A56] text-lg mt-5 max-w-xl">
              Anniversaires, mariages, baptêmes, repas d'entreprise — votre événement
              en exclusivité dans une ambiance chaleureuse, portugaise et conviviale.
              <b className="text-[#2C2B29]"> À partir de {RESTAURANT.privatization.minGuests} personnes.</b>
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={`tel:${RESTAURANT.phone.replace(/\s/g,'')}`} className="btn-secondary" data-testid="priv-call-btn">
                <Phone size={16} className="mr-2"/> {RESTAURANT.phone}
              </a>
              <a href="#priv-form" className="btn-primary">Demander un devis</a>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-80 lg:h-[480px]">
            <img src={ASSETS.bar} alt="Salle du fond — Globo Dancing" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6">
          {[
            { icon: Users, t: "Jusqu'à 80 personnes", d: "Espace modulable selon votre événement, en assis comme en debout-cocktail." },
            { icon: Music2, t: "Ambiance dansante", d: "DJ, sono, piste de danse — la fête portugaise jusqu'à 1h les vendredis et samedis." },
            { icon: Cake, t: "Menus sur mesure", d: "Cuisine maison adaptée à votre budget : buffet, plats à partager, formule complète." },
          ].map((p, i) => (
            <div key={i} className="card-warm p-7" data-testid={`priv-pillar-${i}`}>
              <div className="w-12 h-12 rounded-full bg-[#C04E38]/10 text-[#C04E38] grid place-items-center mb-4"><p.icon size={22}/></div>
              <h3 className="text-2xl mb-2">{p.t}</h3>
              <p className="text-[#5C5A56] leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section id="priv-form" className="py-16 bg-[#F4F1EA]">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <div className="eyebrow">Pedir um orçamento</div>
            <h2 className="text-4xl md:text-5xl mt-3">Demande de devis</h2>
            <p className="text-[#5C5A56] mt-3">Remplissez le formulaire — nous revenons vers vous dans la journée.</p>
          </div>

          <form onSubmit={submit} data-testid="privatization-form" className="card-warm p-8 md:p-10 space-y-5">
            <div>
              <label className="text-sm font-medium mb-2 block">Type d'événement *</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {EVENT_TYPES.map((t) => (
                  <button type="button" key={t.v}
                    onClick={() => setForm({ ...form, event_type: t.v })}
                    data-testid={`event-type-${t.v.toLowerCase().replace(/[^a-z]/g,'-')}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition text-left ${
                      form.event_type === t.v ? "bg-[#C04E38] text-white border-[#C04E38]" : "bg-white border-[#E6E1D6] hover:border-[#C04E38]"
                    }`}>
                    <t.icon size={16}/> <span>{t.v}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Nom complet *</label>
                <input className="input-warm" required value={form.name} onChange={update("name")} data-testid="priv-name"/>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Téléphone *</label>
                <input type="tel" className="input-warm" required value={form.phone} onChange={update("phone")} data-testid="priv-phone"/>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email *</label>
              <input type="email" className="input-warm" required value={form.email} onChange={update("email")} data-testid="priv-email"/>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Date envisagée *</label>
                <input type="date" className="input-warm" required min={minDate} value={form.date} onChange={update("date")} data-testid="priv-date"/>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Personnes *</label>
                <input type="number" min={RESTAURANT.privatization.minGuests} className="input-warm" required value={form.guests} onChange={update("guests")} data-testid="priv-guests"/>
                <div className="text-xs text-[#5C5A56] mt-1">Min. {RESTAURANT.privatization.minGuests} pers.</div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Budget approximatif</label>
                <select className="input-warm" value={form.budget} onChange={update("budget")} data-testid="priv-budget">
                  <option value="">À définir ensemble</option>
                  <option>Moins de 1 000 €</option>
                  <option>1 000 — 2 500 €</option>
                  <option>2 500 — 5 000 €</option>
                  <option>Plus de 5 000 €</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Besoins spécifiques</label>
              <div className="flex flex-wrap gap-2">
                {NEEDS.map((n) => (
                  <button type="button" key={n} onClick={() => toggleNeed(n)} data-testid={`priv-need-${n.replace(/[^a-zA-Z]/g,'')}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${
                      form.needs.includes(n) ? "bg-[#1F476B] text-white border-[#1F476B]" : "bg-white border-[#E6E1D6] hover:border-[#1F476B]"
                    }`}>{n}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Message (détails, allergies, demandes particulières)</label>
              <textarea rows={4} className="input-warm" value={form.message} onChange={update("message")} data-testid="priv-message"/>
            </div>

            <button type="submit" disabled={sending} className="btn-primary w-full md:w-auto" data-testid="priv-submit">
              {sending ? <><Loader2 className="animate-spin mr-2" size={18}/> Envoi…</> : "Envoyer ma demande"}
            </button>
            <p className="text-xs text-[#5C5A56]">Vous pouvez aussi nous appeler directement au <a href={`tel:${RESTAURANT.phone.replace(/\s/g,'')}`} className="text-[#C04E38] font-medium">{RESTAURANT.phone}</a>.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
